import React from "react";
import { useLanguage, useT } from "@/lib/LanguageContext";
// Relative on purpose: pure modules, resolvable under Jest without a shim.
import { formatMoneyCode, intlLocaleFor } from "../../lib/formatMoney";
import { fmtCalendarDate } from "../../pathways/format";
import { estimatedLength, spotsState } from "../../pathways/learnerModel";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";

/**
 * The path type in plain words (contract §5.2, spec screen 6): successive -> "step by
 * step", alternative -> "flexible", level -> "N levels". One literal `t` call per key, so
 * the key-coverage scan sees each of them.
 */
export function PathTypeLabel({ pathType, levelsCount }) {
  const t = useT("public");
  if (pathType === "successive") return t("pathways.type.successive");
  if (pathType === "alternative") return t("pathways.type.alternative");
  return levelsCount === 1 ? t("pathways.type.levelsOne") : t("pathways.type.levelsMany", { n: levelsCount });
}

/** "~6 hours total" / "~1.5 hours total" / "~45 min"; null at 0. */
export function LengthLabel({ minutes }) {
  const t = useT("public");
  const { lang } = useLanguage();
  const length = estimatedLength(minutes);
  if (!length) return null;
  if (length.unit === "minutes") return t("pathways.length.minutes", { n: length.value });
  if (length.value === 1) return t("pathways.length.hoursOne");
  const n = new Intl.NumberFormat(intlLocaleFor(lang), { maximumFractionDigits: 1 }).format(length.value);
  return t("pathways.length.hoursMany", { n });
}

const Chip = ({ children, testId, tone = "plain" }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center rounded-full border px-3 py-1 text-xs ${
      tone === "accent"
        ? "border-eh-ochre-deep/40 bg-eh-ochre/10 text-eh-ink"
        : "border-eh-border-soft bg-eh-surface text-eh-muted"
    }`}
  >
    {children}
  </span>
);

/**
 * One pathway as a card (spec screen 6, "/parcours listing card"), in the wireframe's order:
 *
 *   chips      the type in plain words · the estimated length (· the cohort's start date)
 *   title and the description (contract §2: "shown on /parcours"; the spec's screen-2
 *     field "Description appears on the public /parcours listing")
 *   "By the end you will be able to:" and the objectives
 *   price ("Free" at 0) · spots left (nothing when unlimited, "Full" at 0)
 *   "Join the pathway" (when `onJoin` is given and the pathway is not full)
 *
 * The cohort start date is not in the wireframe; it is shown because a visitor joining a
 * cohort must know when it starts, and the chip row is where the card states its facts.
 *
 * @param {object} props
 * @param {object} props.card  PublicPathwayCard (or PublicPathwayDetail)
 * @param {"h2"|"h1"} [props.as]  the title's heading level (h1 on the pathway's own page)
 * @param {boolean} [props.full]    the whole description (the pathway's own page); the
 *                                  listing clamps it to three lines
 * @param {() => void} [props.onJoin]  shows "Join the pathway"
 * @param {boolean} [props.joinOpen]   the join form below the card is open (aria-expanded)
 */
export default function PathwayCard({ card, as: Heading = "h2", full = false, onJoin, joinOpen = false }) {
  const t = useT("public");
  const { lang } = useLanguage();
  const objectives = Array.isArray(card.objectives) ? card.objectives.filter((o) => typeof o === "string" && o.trim()) : [];
  const spots = spotsState(card.spots_left);
  const start = card.schedule === "cohort" ? fmtCalendarDate(card.start_date, lang) : "";
  const length = estimatedLength(card.estimated_minutes);

  return (
    <article data-testid={IDS.card(card.id)} className="rounded-3xl border border-eh-border-soft bg-eh-cream p-5 sm:p-6">
      <div className="flex flex-wrap gap-2">
        <Chip testId={IDS.cardType(card.id)} tone="accent">
          <PathTypeLabel pathType={card.path_type} levelsCount={card.levels_count} />
        </Chip>
        {length && (
          <Chip testId={IDS.cardDuration(card.id)}>
            <LengthLabel minutes={card.estimated_minutes} />
          </Chip>
        )}
        {start && <Chip>{t("pathways.card.startsOn", { date: start })}</Chip>}
      </div>

      <Heading className="mt-4 font-display text-2xl text-eh-ink leading-snug break-words">{card.title}</Heading>
      {card.description && (
        <p className={`mt-2 text-sm text-eh-muted whitespace-pre-line break-words ${full ? "" : "line-clamp-3"}`}>
          {card.description}
        </p>
      )}

      {objectives.length > 0 && (
        <div className="mt-3 text-sm text-eh-ink">
          <p>{t("pathways.card.objectives")}</p>
          <ul className="mt-1 space-y-0.5">
            {objectives.map((o, i) => (
              // Objectives carry no id on the wire; their position is their identity.
              <li key={`${i}-${o}`} data-testid={IDS.cardObjective(card.id, i)} className="flex gap-2 break-words">
                <span aria-hidden="true">•</span>
                <span className="min-w-0">{o}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <span data-testid={IDS.cardPrice(card.id)} className="font-medium text-eh-ink">
          {card.price_cents > 0 ? formatMoneyCode(card.price_cents, card.currency) : t("pathways.card.free")}
        </span>
        {spots.kind !== "unlimited" && (
          <span
            data-testid={IDS.cardSpots(card.id)}
            className={`text-sm font-medium ${spots.kind === "full" ? "text-rose-700" : "text-eh-ink"}`}
          >
            {spots.kind === "full"
              ? t("pathways.card.full")
              : spots.n === 1 ? t("pathways.card.spotsOne") : t("pathways.card.spotsMany", { n: spots.n })}
          </span>
        )}
      </div>

      {onJoin && spots.kind !== "full" && (
        <button
          type="button"
          onClick={onJoin}
          aria-expanded={joinOpen}
          data-testid={IDS.cardJoin(card.id)}
          className="mt-5 inline-flex h-11 items-center justify-center rounded-full bg-eh-ink px-6 text-sm font-medium text-eh-cream hover:bg-eh-ink/90"
        >
          {t("pathways.card.join")}
        </button>
      )}
    </article>
  );
}
